import { useCallback, useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import {
  confirmTotpEnrollment,
  disableTotp,
  getStrongAuthStatus,
  regenerateRecoveryCodes,
  startTotpEnrollment,
  verifyRecoveryCodeStepUp,
  verifyTotpStepUp,
} from "@/services/strongAuth";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

export const StrongAuthPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loadState, setLoadState] = useState("LOADING");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [recoveryTotpCode, setRecoveryTotpCode] = useState("");

  const loadStatus = useCallback(async () => {
    setError("");
    try {
      const result = await getStrongAuthStatus(token);
      setStatus(result);
      setLoadState("READY");
      return result;
    } catch (loadError) {
      setError(String(loadError?.message || "Strong-Auth-Status konnte nicht geladen werden."));
      setLoadState("ERROR");
      return null;
    }
  }, [token]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const run = async (key, action) => {
    setBusy(key);
    setError("");
    try {
      await action();
    } catch (actionError) {
      setError(String(actionError?.message || "Strong-Auth-Aktion fehlgeschlagen."));
    } finally {
      setBusy("");
    }
  };

  const beginEnrollment = () => run("enroll-start", async () => {
    const result = await startTotpEnrollment(password, token);
    setSetup(result);
    setPassword("");
    setCode("");
  });

  const confirmEnrollment = () => run("enroll-confirm", async () => {
    try {
      const result = await confirmTotpEnrollment(code, token);
      setRecoveryCodes(result?.recovery_codes || []);
      setSetup(null);
      setCode("");
      await loadStatus();
    } catch (confirmError) {
      const latestStatus = await loadStatus();
      if (latestStatus?.enabled) {
        setSetup(null);
        setCode("");
        setRecoveryCodes([]);
      }
      throw confirmError;
    }
  });

  const stepUp = () => run("verify", async () => {
    await verifyTotpStepUp(code, token);
    setCode("");
    await loadStatus();
  });

  const recoveryStepUp = () => run("recovery-verify", async () => {
    await verifyRecoveryCodeStepUp(recoveryCode, token);
    setRecoveryCode("");
    await loadStatus();
  });

  const regenerateCodes = () => run("recovery-regenerate", async () => {
    const result = await regenerateRecoveryCodes(recoveryPassword, recoveryTotpCode, token);
    setRecoveryCodes(result?.recovery_codes || []);
    setRecoveryPassword("");
    setRecoveryTotpCode("");
    await loadStatus();
  });

  const removeTotp = () => run("disable", async () => {
    await disableTotp(disablePassword, disableCode, token);
    setDisablePassword("");
    setDisableCode("");
    setSetup(null);
    setCode("");
    setRecoveryCode("");
    setRecoveryCodes([]);
    setRecoveryPassword("");
    setRecoveryTotpCode("");
    await loadStatus();
  });

  return (
    <AionSectionCard
      title="Starke Authentifizierung"
      eyebrow="HUMAN KEY"
      subtitle="TOTP und einmalige Recovery Codes sichern die bestehende AION Auth-Session. Rollenstatus oder normales Passwort allein gelten nicht als Strong Auth."
      testId="strong-auth-card"
    >
      {error ? <div className="aion-error-box" data-testid="strong-auth-error">{error}</div> : null}

      {loadState === "ERROR" && !status ? (
        <AionEmptyState
          icon={KeyRound}
          title="Strong Auth ist gerade nicht verfügbar."
          description="AION gibt ohne verifizierten zweiten Faktor keine Strong-Auth-Freigabe aus."
          testId="strong-auth-error-state"
        />
      ) : null}

      {status ? (
        <div className="aion-note" data-testid="strong-auth-status-note">
          <ShieldCheck aria-hidden="true" className="aion-list-icon" />
          Authenticator: {status.enabled ? "EINGERICHTET" : "NICHT EINGERICHTET"}
          {status.session_strong_auth_expires_at ? ` · Session Strong Auth bis ${formatTimestamp(status.session_strong_auth_expires_at)}` : " · Session Strong Auth nicht aktiv"}
          {status.enabled ? ` · Recovery Codes: ${status.recovery_codes_remaining ?? 0}` : ""}
        </div>
      ) : null}

      {!status?.enabled && !setup ? (
        <div className="aion-settings-grid" data-testid="strong-auth-enrollment-start">
          <label className="aion-settings-field">
            <span>Passwort erneut bestätigen</span>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="strong-auth-password-input"
            />
          </label>
          <div className="aion-settings-field">
            <span>Authenticator einrichten</span>
            <Button
              onClick={() => void beginEnrollment()}
              disabled={!password || busy === "enroll-start"}
              data-testid="strong-auth-enroll-start-button"
            >
              {busy === "enroll-start" ? "Prüft …" : "TOTP einrichten"}
            </Button>
          </div>
        </div>
      ) : null}

      {setup ? (
        <div className="aion-view-stack" data-testid="strong-auth-enrollment-setup">
          <div className="aion-note">
            Trage diesen Schlüssel einmalig in deine Authenticator-App ein. AION speichert ihn serverseitig nur verschlüsselt; diese Anzeige bleibt nur im aktuellen Komponenten-Zustand.
          </div>
          <label className="aion-settings-field">
            <span>Authenticator-Schlüssel</span>
            <Input readOnly value={setup.secret || ""} data-testid="strong-auth-setup-secret" />
          </label>
          <label className="aion-settings-field">
            <span>Setup URI</span>
            <Input readOnly value={setup.otpauth_uri || ""} data-testid="strong-auth-setup-uri" />
          </label>
          <label className="aion-settings-field">
            <span>6-stelliger Code</span>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="strong-auth-enroll-code-input"
            />
          </label>
          <Button
            onClick={() => void confirmEnrollment()}
            disabled={code.length !== 6 || busy === "enroll-confirm"}
            data-testid="strong-auth-enroll-confirm-button"
          >
            {busy === "enroll-confirm" ? "Bestätigt …" : "Authenticator bestätigen"}
          </Button>
        </div>
      ) : null}

      {recoveryCodes.length ? (
        <div className="aion-view-stack" data-testid="strong-auth-recovery-codes-once">
          <div className="aion-note">
            <strong>Recovery Codes – nur jetzt sichtbar.</strong>
            <br />
            Speichere diese Codes sicher außerhalb von AION. Jeder Code funktioniert genau einmal. AION speichert serverseitig nur Hashes.
          </div>
          <div className="aion-settings-grid" data-testid="strong-auth-recovery-code-list">
            {recoveryCodes.map((item, index) => (
              <Input key={item} readOnly value={item} data-testid={`strong-auth-recovery-code-${index}`} />
            ))}
          </div>
          <Button type="button" variant="outline" onClick={() => setRecoveryCodes([])} data-testid="strong-auth-recovery-codes-dismiss">
            Ich habe die Codes sicher gespeichert
          </Button>
        </div>
      ) : null}

      {status?.enabled && !setup ? (
        <div className="aion-view-stack" data-testid="strong-auth-step-up">
          <label className="aion-settings-field">
            <span>Authenticator-Code</span>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="strong-auth-step-up-code-input"
            />
          </label>
          <Button
            onClick={() => void stepUp()}
            disabled={code.length !== 6 || busy === "verify"}
            data-testid="strong-auth-step-up-button"
          >
            {busy === "verify" ? "Verifiziert …" : "Strong Auth für 10 Minuten aktivieren"}
          </Button>

          <div className="aion-settings-grid" data-testid="strong-auth-recovery-step-up">
            <label className="aion-settings-field">
              <span>Recovery Code</span>
              <Input
                autoComplete="off"
                value={recoveryCode}
                onChange={(event) => setRecoveryCode(event.target.value.toUpperCase().slice(0, 32))}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                data-testid="strong-auth-recovery-code-input"
              />
            </label>
            <div className="aion-settings-field">
              <span>Backup-Zugang</span>
              <Button
                variant="outline"
                onClick={() => void recoveryStepUp()}
                disabled={recoveryCode.replace(/[^A-Z0-9]/g, "").length < 12 || busy === "recovery-verify"}
                data-testid="strong-auth-recovery-verify-button"
              >
                {busy === "recovery-verify" ? "Verifiziert …" : "Recovery Code verwenden"}
              </Button>
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="strong-auth-recovery-regenerate-open-button">Neue Recovery Codes erzeugen</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Neue Recovery Codes erzeugen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Alle bisherigen Recovery Codes werden sofort ungültig. Dafür sind dein aktuelles Passwort und ein frischer TOTP-Code erforderlich.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="aion-view-stack">
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Aktuelles Passwort"
                  value={recoveryPassword}
                  onChange={(event) => setRecoveryPassword(event.target.value)}
                  data-testid="strong-auth-recovery-regenerate-password"
                />
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-stelliger TOTP-Code"
                  value={recoveryTotpCode}
                  onChange={(event) => setRecoveryTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="strong-auth-recovery-regenerate-totp"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void regenerateCodes()}
                  disabled={!recoveryPassword || recoveryTotpCode.length !== 6 || busy === "recovery-regenerate"}
                  data-testid="strong-auth-recovery-regenerate-confirm"
                >
                  {busy === "recovery-regenerate" ? "Erzeugt …" : "Alte Codes ersetzen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="strong-auth-disable-open-button">Authenticator entfernen</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Authenticator wirklich entfernen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dafür werden dein aktuelles Passwort und ein gültiger TOTP-Code verlangt. Danach werden bestehende Strong-Auth-Beweise deiner aktiven Sessions gelöscht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="aion-view-stack">
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Aktuelles Passwort"
                  value={disablePassword}
                  onChange={(event) => setDisablePassword(event.target.value)}
                  data-testid="strong-auth-disable-password-input"
                />
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-stelliger Code"
                  value={disableCode}
                  onChange={(event) => setDisableCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="strong-auth-disable-code-input"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void removeTotp()}
                  disabled={!disablePassword || disableCode.length !== 6 || busy === "disable"}
                  data-testid="strong-auth-disable-confirm-button"
                >
                  {busy === "disable" ? "Entfernt …" : "Authenticator entfernen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : null}
    </AionSectionCard>
  );
};
